import { Component, Inject, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormGroupDefinition } from 'components';
import { asyncScheduler, Observable, of } from 'rxjs';
import { first, map, observeOn, take } from 'rxjs/operators';
import { NotificationService } from 'src/app/core/services/notification.service';
import { CodeWrapperService } from 'src/app/core/services/service-wrappers/code-wrapper.service';

@Component({
  standalone: false,
  selector: 'app-revenue-code-dialog',
  templateUrl: './revenue-code-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./revenue-code-dialog.component.scss']
})
export class RevenueCodeDialogComponent implements OnInit {
  formGroup = new UntypedFormGroup({});
  formInitialized = false;
  saving = false;
  CODE_TYPE = 'RevenueCode';
  revenueCodeId;
  viewOnly = false;

  formDefinitions: FormGroupDefinition[];
  getFormDefinitions(isEdit: boolean): FormGroupDefinition[] {
    return [
      {
        hideTitle: true,
        controls: [
          {
            label: 'Code',
            name: 'code',
            isReadOnly: isEdit,
            type: 'text',
            class: 'form-span-6',
            validators: [Validators.required, Validators.maxLength(4)]
          },
          {
            label: 'Active',
            name: 'active',
            type: 'checkbox',
            class: 'form-span-6',
            initial: true,
            validators: Validators.required
          },
          {
            label: 'Description',
            name: 'name',
            type: 'text',
            class: 'form-span-12',
            validators: Validators.required
          },
          {
            label: 'Send Procedure Code',
            name: 'sendProcedureCode',
            type: 'checkbox',
            class: 'form-span-12',
            initial: true
          },
          {
            label: 'Suppress Units',
            name: 'suppressUnits',
            type: 'checkbox',
            class: 'form-span-12',
            initial: false
          }
        ]
      }
    ];
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private injector: Injector,
    private notificationService: NotificationService,
    public dialogRef: MatDialogRef<RevenueCodeDialogComponent>,
    private service: CodeWrapperService
  ) {
    this.revenueCodeId = data?.revenueCodeId;
    const isEdit = this.revenueCodeId && this.revenueCodeId !== '';
    this.viewOnly = data?.viewOnly;
    this.formDefinitions = this.getFormDefinitions(isEdit);
  }

  ngOnInit(): void {
    this.setupForm();

    if (this.revenueCodeId && this.revenueCodeId !== '') {
      this.service
        .apiV1CodeCodetypeIdGet(this.CODE_TYPE, this.revenueCodeId)
        .pipe(first())
        .subscribe((result) => {
          this.formGroup.patchValue(result);
          this.formGroup.get('active').patchValue(!result.inactive);
        });
    }
  }

  setupForm() {
    this.formDefinitions.forEach((sc) => {
      sc.controls.forEach((control) => {
        if (control.type !== 'empty' && control.type !== 'label') {
          this.formGroup.addControl(control.name, new UntypedFormControl(control.initial ?? '', control.validators));
        }
      });
    });
    this.formInitialized = true;
  }

  save() {
    if (this.formGroup.valid && this.saving === false) {
      this.saving = true;

      const formData = {
        code: this.formGroup.get('code').value,
        name: this.formGroup.get('name').value,
        inactive: this.formGroup.get('active').value === true ? false : true,
        sendProcedureCode: this.formGroup.get('sendProcedureCode').value ?? false,
        suppressUnits: this.formGroup.get('suppressUnits').value ?? false
      };

      let saveObservable: Observable<any>;
      let notification;

      if (this.revenueCodeId) {
        notification = 'Revenue Code Updated';
        saveObservable = this.service
          .apiV1CodeCodetypePatchIdPatch(this.CODE_TYPE, this.revenueCodeId, [
            {
              value: formData.name,
              path: 'name',
              op: 'add'
            },
            {
              value: formData.code,
              path: 'code',
              op: 'add'
            },
            {
              value: formData.inactive,
              path: 'inactive',
              op: 'add'
            },
            {
              value: formData.sendProcedureCode,
              path: 'sendProcedureCode',
              op: 'add'
            },
            {
              value: formData.suppressUnits,
              path: 'suppressUnits',
              op: 'add'
            }
          ])
          .pipe(
            map((x: any) => {
              x;
            })
          );
      } else {
        notification = 'Revenue Code Added';
        saveObservable = this.service.apiV1CodeCodetypeAddPost(this.CODE_TYPE, formData).pipe(map((x: any) => x));
      }

      saveObservable
        .pipe(take(1), observeOn(asyncScheduler))
        .subscribe(
          (response) => {
            this.dialogRef.close(true);
            this.notificationService.success(notification);
          },
          (err) => {
            this.notificationService.error('Error Saving Revenue Code');
            this.saving = false;
          }
        );
    }
  }
}
